import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import {
  Search,
  ChevronUp,
  ChevronDown,
  Download,
  Settings,
  BookOpen,
  Maximize,
  Type,
  Palette,
  Loader2,
  AlertCircle,
  ZoomIn,
  ZoomOut
} from 'lucide-react';

interface TxtReaderProps {
  url: string;
  title: string;
}

interface ReaderSettings {
  fontSize: number;
  fontFamily: string;
  lineHeight: number;
  theme: 'light' | 'dark' | 'sepia';
  textAlign: 'left' | 'center' | 'justify';
  maxWidth: number;
}

const TxtReader: React.FC<TxtReaderProps> = ({ url, title }) => {
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  const [settings, setSettings] = useState<ReaderSettings>({
    fontSize: 16,
    fontFamily: 'serif',
    lineHeight: 1.6,
    theme: 'light',
    textAlign: 'left',
    maxWidth: 800,
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRefs = useRef<HTMLSpanElement[]>([]);

  // Process and enhance text content for better readability
  const processTextContent = (text: string): string => {
    // Remove excessive line breaks while preserving paragraph structure
    text = text.replace(/\n\s*\n\s*\n+/g, '\n\n');
    
    // Fix common formatting issues from PDF conversion
    text = text.replace(/([a-z])\n([A-Z])/g, '$1 $2'); // Join broken words
    text = text.replace(/\s+/g, ' '); // Normalize spaces
    text = text.replace(/\n /g, '\n'); // Remove leading spaces after newlines
    
    // Enhance chapter/section detection
    text = text.replace(/^(Chapter|CHAPTER|Section|SECTION)\s*(\d+|[IVX]+)/gm, '\n\n$1 $2');
    
    // Improve paragraph spacing around titles/headers
    text = text.replace(/^([A-Z][A-Z\s]{3,})$/gm, '\n\n$1\n');
    
    // Add proper spacing after periods if missing
    text = text.replace(/\.([A-Z])/g, '. $1');
    
    // Fix common Arabic text formatting issues
    text = text.replace(/(\u0621-\u064A)\s+(\u0621-\u064A)/g, '$1$2'); // Fix Arabic spacing
    
    return text.trim();
  };

  useEffect(() => {
    const controller = new AbortController();
    
    const loadTextContent = async () => {
      setIsLoading(true);
      setLoadError(null);
      
      try {
        const response = await fetch(url, { 
          signal: controller.signal,
          cache: 'force-cache' // Use cache when possible
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        let text = await response.text();
        
        // Enhanced text processing for better readability
        text = processTextContent(text);
        
        setContent(text);
        toast.success('📖 Text loaded successfully!');
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return; // Request was aborted, no need to update state
        }
        
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'Failed to load text file';
          
        setLoadError('Failed to load text file. Please check your internet connection and try again.');
        toast.error('Failed to load text file');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    if (url) {
      loadTextContent();
    }
    
    // Cleanup function to abort fetch on unmount or URL change
    return () => {
      controller.abort();
    };
  }, [url]);

  useEffect(() => {
    if (searchTerm && content) {
      const regex = new RegExp(searchTerm, 'gi');
      const matches = [...content.matchAll(regex)];
      setSearchResults(matches.map(match => match.index!));
      setCurrentSearchIndex(0);
    } else {
      setSearchResults([]);
      setCurrentSearchIndex(0);
    }
  }, [searchTerm, content]);

  useEffect(() => {
    if (searchResults.length > 0 && contentRef.current) {
      const currentMatch = searchRefs.current[currentSearchIndex];
      if (currentMatch) {
        currentMatch.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
  }, [currentSearchIndex, searchResults]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      goToNextSearchResult();
    }
  };

  const goToNextSearchResult = () => {
    if (searchResults.length > 0) {
      setCurrentSearchIndex((prev) => (prev + 1) % searchResults.length);
    }
  };

  const goToPrevSearchResult = () => {
    if (searchResults.length > 0) {
      setCurrentSearchIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
    }
  };

  const highlightSearchResults = (text: string) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);

    let matchIndex = 0;
    return parts.map((part, index) => {
      if (regex.test(part)) {
        const isCurrentMatch = matchIndex === currentSearchIndex;
        const element = (
          <span
            key={index}
            ref={(el) => {
              if (el) searchRefs.current[matchIndex] = el;
            }}
            className={`${
              isCurrentMatch
                ? 'bg-yellow-400 text-black'
                : 'bg-yellow-200 dark:bg-yellow-800'
            } px-1 rounded`}
          >
            {part}
          </span>
        );
        matchIndex++;
        return element;
      }
      return part;
    });
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      exitFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setIsFullscreen(false);
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Download started!');
  };

  const updateSetting = (key: keyof ReaderSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const getThemeStyles = () => {
    switch (settings.theme) {
      case 'dark':
        return 'bg-gray-900 text-gray-100';
      case 'sepia':
        return 'bg-amber-50 text-amber-900';
      default:
        return 'bg-white text-gray-900';
    }
  };

  const getFontFamily = () => {
    switch (settings.fontFamily) {
      case 'serif':
        return 'font-serif';
      case 'mono':
        return 'font-mono';
      default:
        return 'font-sans';
    }
  };

  const getTextAlign = () => {
    switch (settings.textAlign) {
      case 'center':
        return 'text-center';
      case 'justify':
        return 'text-justify';
      default:
        return 'text-left';
    }
  };

  if (loadError) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Error Loading Text</h3>
          <p className="text-muted-foreground mb-4">{loadError}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardContent className="p-8 text-center">
          <Loader2 className="h-16 w-16 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Loading Text...</h3>
          <p className="text-muted-foreground">Please wait while we load your text file.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div ref={containerRef} className={`w-full ${isFullscreen ? 'fixed inset-0 z-50 bg-background' : ''}`}>
      {/* Controls */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search text..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-48"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={goToPrevSearchResult}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {currentSearchIndex + 1} of {searchResults.length}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={goToNextSearchResult}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </form>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(!showSettings)}
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={toggleFullscreen}>
                <Maximize className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="mt-4 p-4 border rounded-lg bg-muted/50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Font Size */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Type className="h-4 w-4" />
                    Font Size: {settings.fontSize}px
                  </label>
                  <Slider
                    value={[settings.fontSize]}
                    onValueChange={([value]) => updateSetting('fontSize', value)}
                    min={12}
                    max={24}
                    step={1}
                  />
                </div>

                {/* Font Family */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Font Family</label>
                  <Select
                    value={settings.fontFamily}
                    onValueChange={(value) => updateSetting('fontFamily', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="serif">Serif</SelectItem>
                      <SelectItem value="sans">Sans Serif</SelectItem>
                      <SelectItem value="mono">Monospace</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Theme */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Theme
                  </label>
                  <Select
                    value={settings.theme}
                    onValueChange={(value) => updateSetting('theme', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="sepia">Sepia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Line Height */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Line Height: {settings.lineHeight}
                  </label>
                  <Slider
                    value={[settings.lineHeight]}
                    onValueChange={([value]) => updateSetting('lineHeight', value)}
                    min={1.2}
                    max={2.0}
                    step={0.1}
                  />
                </div>

                {/* Text Alignment */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Text Alignment</label>
                  <Select
                    value={settings.textAlign}
                    onValueChange={(value) => updateSetting('textAlign', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="justify">Justify</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Max Width */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Max Width: {settings.maxWidth}px
                  </label>
                  <Slider
                    value={[settings.maxWidth]}
                    onValueChange={([value]) => updateSetting('maxWidth', value)}
                    min={600}
                    max={1200}
                    step={50}
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content */}
      <Card className={`${isFullscreen ? 'h-full flex flex-col' : ''}`}>
        <CardContent className={`p-6 ${isFullscreen ? 'flex-1 overflow-auto' : ''}`}>
          <div
            ref={contentRef}
            className={`
              mx-auto prose prose-lg
              ${getThemeStyles()}
              ${getFontFamily()}
              ${getTextAlign()}
              whitespace-pre-wrap
            `}
            style={{
              fontSize: `${settings.fontSize}px`,
              lineHeight: settings.lineHeight,
              maxWidth: `${settings.maxWidth}px`,
            }}
          >
            {highlightSearchResults(content)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TxtReader;